import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ToolboxTalkTemplateModel } from '../../models/toolbox-talk-template.model';
import { UtilsService } from '../../shared/utils.service';
import { AppService } from '../../app.service';
import { ApiRequestService } from '../../shared/api-request.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToolboxTalksTemplatesEditComponent } from '../toolbox-talks-templates-edit/toolbox-talks-templates-edit.component';
import { RBACService } from '../../shared/rbac.service';

@Component({
  selector: 'app-toolbox-talks-templates-tabs',
  templateUrl: './toolbox-talks-templates-tabs.component.html',
  styleUrls: ['./toolbox-talks-templates-tabs.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ToolboxTalksTemplatesTabsComponent implements OnInit {
  toolbox_talk: ToolboxTalkTemplateModel = {} as ToolboxTalkTemplateModel;
  path: string;
  urlPath: string;

  // RBAC permission flags
  canEdit: boolean = false;
  canDelete: boolean = false;
  canView: boolean = false;

  constructor(
    public utils: UtilsService,
    public app: AppService,
    private api: ApiRequestService,
    private route: ActivatedRoute,
    public router: Router,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    // Resolve RBAC permission flags.
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_TOOLBOX_TALKS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_TOOLBOX_TALKS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_TOOLBOX_TALKS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );
    // get the toolbox talks id from the parent route params
    this.toolbox_talk.id = Number(
      this.route.parent.snapshot.children[0].params.toolbox_talk_id
    );

    this.urlPath = this.route.snapshot.url[0].path;

    if (!this.toolbox_talk.id) {
      this.redirectBack();
      return;
    }

    if (this.toolbox_talk.id) {
      this.find(this.toolbox_talk.id);
    }
  }

  private find(toolbox_talk_id: number) {
    this.api
      .makeRequest('get', `v1/toolbox-talks/${toolbox_talk_id}`, [], {
        template: 'true'
      })
      .then((response) => {
        this.toolbox_talk = response.data;
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  public onEdit(toolbox_talk_id: number | string) {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit ' +
          this.utils.getLangTerm('toolbox-talks.singular', 'Toolbox Talk') +
          ' templates.'
      );
      return;
    }
    this.utils.showComponentDialog(
      ToolboxTalksTemplatesEditComponent,
      {
        id: toolbox_talk_id
      },
      {},
      () => {
        this.find(Number(toolbox_talk_id));
      }
    );
  }

  public onRemove() {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete ' +
          this.utils.getLangTerm('toolbox-talks.singular', 'Toolbox Talk') +
          ' templates.'
      );
      return;
    }
    this.utils.showModal(
      `Remove ${this.utils.getLangTerm(
        'toolbox-talks.singular',
        'Toolbox Talk'
      )}`,
      `Are you sure you want to remove this ${this.utils.getLangTerm(
        'toolbox-talks.singular',
        'Toolbox Talk'
      )}?`,
      () => {
        this.remove(this.toolbox_talk.id);
      }
    );
  }

  private remove(id: number) {
    this.api
      .makeRequest('delete', `v1/toolbox-talks/${id}`, [], {
        template: 'true'
      })
      .then((response) => {
        this.utils.showToast(
          `The ${this.utils.getLangTerm(
            'toolbox-talks.singular',
            'Toolbox Talk'
          )} was removed`
        );
        this.redirectBack();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  private redirectBack() {
    this.router.navigate([`/toolbox-talks-templates/`]);
  }

  public onExport(type: string = 'pdf') {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to export ' +
          this.utils.getLangTerm('toolbox-talks.singular', 'Toolbox Talk') +
          ' templates.'
      );
      return;
    }
    if (!this.toolbox_talk.id) {
      this.utils.showModal(
        'Error Message',
        'We are unable to export the ' +
          this.utils.getLangTerm('toolbox-talks.singular', 'Toolbox Talk') +
          ' template. Please try again or contact us at ' +
          this.utils.getSupportEmailAddress() +
          ' for assistance.'
      );
      return;
    }

    this.api
      .makeDownloadRequest(
        `v1/toolbox-talks/export/${type}/` + this.toolbox_talk.id,
        {},
        {
          template_type: 'all'
        }
      )
      .then((response) => {
        // Get the current date object
        const date = new Date();

        // Create object url to handle file downloads
        const data = window.URL.createObjectURL(response);

        // Create a download link
        const downloadLink = document.createElement('a');
        downloadLink.href = data;
        downloadLink.download =
          this.utils.getLangTerm('toolbox-talks.plural', 'Toolbox Talks') +
          ` - ${date.getFullYear()}${date.getMonth() +
            1}${date.getDate()}.${type}`;
        // Initiate the download
        downloadLink.click();

        // For Firefox it is necessary to delay revoking the ObjectURL
        setTimeout(function() {
          window.URL.revokeObjectURL(data);
        }, 300); // Minimum 300 miliseconds
      })
      .catch((errorResponse) => {
        this.utils.showModal('Error', errorResponse.message);
      });
  }
}
